import { useState, useEffect } from 'react';
import { Box, Typography, Tabs, Tab, Grid, useTheme, Paper, Alert } from '@mui/material';
import { StyledContainer } from '../dynamicform/styled.js';
import Title from './shared/Title.jsx';
import TopBanner from './shared/TopBanner.jsx';
import { validateAuth, getApiBaseUrl, fetchData } from '../util/util.js';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { userTypeSelector } from '../redux/selectors.js';
import { notificationError } from '../redux/actions/app.js';
import DescriptionIcon from '@mui/icons-material/Description';
import PolicyIcon from '@mui/icons-material/Policy';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FaqTab from './Settings/FaqTab.jsx';
import moment from 'moment';

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index} aria-labelledby={`help-tab-${index}`}>
      {value === index && (
        <Box sx={{ pt: 2, backgroundColor: 'background.paper', minHeight: 'calc(100vh - 200px)' }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const Help = () => {
  const [tab, setTab] = useState(process.env.REACT_APP_FF_DISABLEFAQ_050726 === 'true' ? 1 : 0);
  const [contactTeams, setContactTeams] = useState([]);
  const [contactTeamsError, setContactTeamsError] = useState('');
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const currentUserType = useSelector(userTypeSelector);
  const dispatch = useDispatch();
  const theme = useTheme();
  const [releaseNotes, setReleaseNotes] = useState([]);

  const fetchReleaseNotes = () => {
    fetchData(
      'get',
      `/release_notes`,
      {},
      (data) => {
        setReleaseNotes(data);
      },
      currentUserType.type
    );
  };

  useEffect(() => {
    fetchReleaseNotes();
  }, []);

  const getContactTeams = async () => {
    if (!currentUserType) return;

    setContactTeamsError('');
    setIsLoadingTeams(true);
    return validateAuth(async (session) => {
      try {
        const response = await axios.get(`${getApiBaseUrl(currentUserType.type)}/contact_teams`, {
          headers: {
            Authorization: session.getIdToken().getJwtToken(),
          },
          timeout: 15000,
        });

        setContactTeams(response?.data || []);
      } catch (error) {
        dispatch(notificationError('Unable to load contact teams.'));
        setContactTeams([]);
        setContactTeamsError('Unable to load contact teams.');
      } finally {
        setIsLoadingTeams(false);
      }
    });
  };

  useEffect(() => {
    getContactTeams();
  }, [currentUserType]);

  const formatCategoryLabel = (category) => {
    if (!category) return '';

    const formatted = category
      .split('_')
      .join(' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    return formatted.toLowerCase() === 'operational escalations' ||
      formatted.toLowerCase().includes('team')
      ? formatted
      : `${formatted} Team`;
  };

  const contactTeamCards = contactTeams;

  const renderTeamCard = (team, category, isOperationsTeam, isOperationalEscalationsTeam) => {
    const isEscalationCategory =
      category.category?.trim().toLowerCase() === 'operational escalations';

    return (
      <Grid key={team.team} size={{ xs: 12, md: 4 }}>
        <StyledContainer
          style={{
            padding: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            height: 'auto',
          }}
        >
          {!isOperationsTeam && !isOperationalEscalationsTeam && !isEscalationCategory && (
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              {formatCategoryLabel(category.category)}
            </Typography>
          )}

          {(isOperationsTeam || isOperationalEscalationsTeam) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {team.team}
              </Typography>
              {team.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" color="tertiary" />
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    <a
                      href={`mailto:${team.email}`}
                      style={{
                        color: 'inherit',
                        textDecoration: 'underline',
                      }}
                    >
                      {team.email}
                    </a>
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {team.description && !isOperationsTeam && !isOperationalEscalationsTeam && (
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: 'text.secondary',
                lineHeight: 1.6,
                flexGrow: 1,
              }}
            >
              {team.description}
            </Typography>
          )}

          {!isOperationsTeam && !isOperationalEscalationsTeam && team.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
              <EmailIcon fontSize="small" color="tertiary" />
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <a
                  href={`mailto:${team.email}`}
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                  }}
                >
                  {team.email}
                </a>
              </Typography>
            </Box>
          )}
        </StyledContainer>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        marginLeft: '72px',
        gap: '16px',
        width: '100%',
      }}
    >
      <TopBanner bannerVariant="dashboard" />

      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          overflow: 'auto',
          height: 'calc(100vh - 55px)',
          minWidth: 'calc(100vw - 72px - 32px)',
          backgroundColor: 'background.default',
        }}
        elevation={4}
        variant="outlined"
      >
        <Title title="Help" />

        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {process.env.REACT_APP_FF_DISABLEFAQ_050726 !== 'true' && (
            <Tab label="FAQ" id="help-tab-0" value={0} />
          )}
          <Tab label="Contact Us" id="help-tab-1" value={1} />
          <Tab label="Terms & Conditions" id="help-tab-2" value={2} />
          <Tab label="About" id="help-tab-3" value={3} />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <FaqTab page="help" />
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box sx={{ p: theme.spacing(3), maxWidth: 1140, width: '100%' }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
              Contact Our Friendly Teams
            </Typography>
            <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.7 }}>
              We're here to help. Whether you have a question about using the portal, need
              assistance with an issue, or would like to share feedback, our teams are ready to
              support you. Get in touch using the contact details below and we'll ensure you get the
              help you need as quickly as possible.
            </Typography>

            <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <StyledContainer
                  style={{
                    padding: theme.spacing(2.5),
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PhoneIcon fontSize="small" color="tertiary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Phone Support
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="body2">
                      🇳🇿 <strong>New Zealand:</strong>{' '}
                      <a
                        href="tel:0800888956"
                        style={{ color: 'inherit', textDecoration: 'underline' }}
                      >
                        0800 888 956
                      </a>
                    </Typography>
                    <Typography variant="body2">
                      🇦🇺 <strong>Australia:</strong>{' '}
                      <a
                        href="tel:1300419928"
                        style={{ color: 'inherit', textDecoration: 'underline' }}
                      >
                        1300 419 928
                      </a>
                    </Typography>
                  </Box>
                </StyledContainer>
              </Grid>
            </Grid>

            {isLoadingTeams && contactTeams.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Loading team contact information...
              </Typography>
            ) : contactTeamsError && contactTeams.length === 0 ? (
              <Alert severity="error">{contactTeamsError}</Alert>
            ) : (
              (() => {
                const categories = [...contactTeamCards].sort(
                  (a, b) => (a.category_order || 999) - (b.category_order || 999)
                );

                // Separate teams into groups
                const standardCategories = [];
                const operationsCategory = [];
                const escalationsCategory = [];

                categories.forEach((category) => {
                  const categoryLower = category.category?.trim().toLowerCase();
                  if (categoryLower === 'operations') {
                    operationsCategory.push(category);
                  } else if (categoryLower === 'Operational Escalations'.toLowerCase()) {
                    escalationsCategory.push(category);
                  } else {
                    standardCategories.push(category);
                  }
                });

                return (
                  <>
                    {/* Primary Support Teams Row */}
                    {standardCategories.length > 0 && (
                      <Grid container spacing={3} sx={{ mb: 6 }}>
                        {standardCategories.map((category) => {
                          const teams = [...(category.items || [])].sort(
                            (a, b) => (a.category_order || 999) - (b.category_order || 999)
                          );
                          return teams.map((team) => renderTeamCard(team, category, false, false));
                        })}
                      </Grid>
                    )}

                    {/* Operations Team */}
                    {operationsCategory.map((category) => {
                      const teams = [...(category.items || [])].sort(
                        (a, b) => (a.category_order || 999) - (b.category_order || 999)
                      );

                      if (!teams.length) return null;

                      return (
                        <Box key={category.category} sx={{ mb: 6 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                            Operations Team
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.7 }}
                          >
                            For local support and coordination, or assistance with service requests
                            our Operations teams are here to help. Contact your data centre team for
                            assistance.
                          </Typography>

                          <Grid container spacing={3}>
                            {teams.map((team) => renderTeamCard(team, category, true, false))}
                          </Grid>
                        </Box>
                      );
                    })}

                    {/* Operational Escalations */}
                    {escalationsCategory.map((category) => {
                      const teams = [...(category.items || [])].sort(
                        (a, b) => (a.category_order || 999) - (b.category_order || 999)
                      );

                      if (!teams.length) return null;

                      return (
                        <Box key={category.category} sx={{ mb: 6 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                            Operational Escalations
                          </Typography>

                          <Grid container spacing={3}>
                            {teams.map((team) => renderTeamCard(team, category, false, false))}
                          </Grid>
                        </Box>
                      );
                    })}
                  </>
                );
              })()
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Grid container spacing={5} sx={{ padding: theme.spacing(3) }}>
            {[
              {
                label: 'Terms and Conditions',
                Icon: DescriptionIcon,
                url: 'https://datacom.com/nz/en/legal/data-centre-app-terms',
              },
              {
                label: 'Privacy Policy',
                Icon: PolicyIcon,
                url: 'https://datacom.com/nz/en/legal/privacy-policy',
              },
            ].map(({ Icon, label, url }) => (
              <Grid size={{ xs: 12, md: 4 }} key={label}>
                <StyledContainer
                  style={{
                    padding: theme.spacing(2),
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'auto',
                    minHeight: '120px',
                    boxSizing: 'border-box',
                  }}
                  onClick={() => window.open(url, '_blank')}
                >
                  <Box sx={{ mr: 3, display: 'flex', alignItems: 'center' }}>
                    <Icon color="tertiary" />
                  </Box>
                  <Box>
                    <Typography variant="overline">{label}</Typography>
                  </Box>
                </StyledContainer>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <Box sx={{ p: theme.spacing(3), display: 'flex', justifyContent: 'flex-start' }}>
            <Box
              sx={{
                width: '100%',
                maxWidth: 920,
              }}
            >
              {releaseNotes.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Version
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, mt: 1, color: 'text.secondary' }}>
                    {`©${new Date().getFullYear()} Data Centres ${currentUserType.type === 'external' ? 'Customer Portal' : 'Admin Portal'} v${releaseNotes[0].version}`}
                  </Typography>

                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Release Notes
                  </Typography>
                  {releaseNotes.map((version) => (
                    <div className="col" key={version.version}>
                      <Typography
                        variant="overline"
                        sx={{ mt: 1 }}
                      >{`${currentUserType.type === 'external' ? 'Customer Portal' : 'Admin Portal'} v${version.version}`}</Typography>
                      <div className="col gap-sm" style={{ marginLeft: '32px' }}>
                        {version.notes.map((note) => (
                          <Typography component="li" variant="body2" key={note}>
                            {note}
                          </Typography>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {currentUserType.type === 'external' && (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, marginTop: 2 }}>
                    Licensing
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    Licensing Information can be provided upon request.
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Help;
